import { useCallback, useEffect, useRef, useState } from "react";

type RecordingContent = { blob: Blob; url: string };

/** 按需使用鉴权请求读取录音，播放和下载共用一次加载；卸载时取消请求并释放 Blob URL。 */
export function useRecordingContent(loadContent: (signal: AbortSignal) => Promise<Blob>) {
  const content = useRef<RecordingContent | null>(null);
  const pending = useRef<Promise<RecordingContent> | null>(null);
  const controller = useRef<AbortController | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(
    () => () => {
      controller.current?.abort();
      if (content.current) URL.revokeObjectURL(content.current.url);
      content.current = null;
      pending.current = null;
    },
    [],
  );

  const getContent = useCallback((): Promise<RecordingContent> => {
    if (content.current) return Promise.resolve(content.current);
    if (pending.current) return pending.current;
    const request = new AbortController();
    controller.current = request;
    setLoading(true);
    const promise = loadContent(request.signal)
      .then((blob) => {
        request.signal.throwIfAborted();
        if (blob.size === 0) throw new Error("Empty recording");
        const loaded = { blob, url: URL.createObjectURL(blob) };
        content.current = loaded;
        return loaded;
      })
      .finally(() => {
        if (controller.current === request) {
          pending.current = null;
          if (!request.signal.aborted) setLoading(false);
        }
      });
    pending.current = promise;
    return promise;
  }, [loadContent]);

  return { getContent, loading };
}
